import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import assign from 'lodash/assign';
import {tt} from '../../../../../../common/getTranslations';
import {PagewebErrorType} from '../../../../../../errors/PagewebErrorType.enum';
import {TalkViewerErrorType} from '../../../../../../errors/TalkViewerErrorType.enum';
import {ViewerInfo} from '../../../../../../graphql/generated/types';
import {listenerMiddleware} from '../../../../../../state/listenerMiddleware';
import {
  DEFAULT_BOTTOM_VIEWER_MENU_HEIGHT_PX,
  DEFAULT_TOP_VIEWER_MENU_HEIGHT_PX,
  LOCAL_STORAGE_KEY_AUTO_TAP_TOOLTIP_HIDDEN,
  TALK_VIEWER_ERROR_MESSAGE_MAP,
  TALK_VIEWER_STATE_NAME,
} from '../constant/talkViewerConstant';
import {AutoTapSpeed} from '../type/AutoTapSpeed.enum';
import {AutoTapType} from '../type/AutoTapType.enum';
import {Exposed} from '../type/Exposed.interface';
import {FontLoadStatus} from '../type/FontLoadStatus.enum';
import {Fullscreen} from '../type/Fullscreen.interface';
import {
  ProductKey,
  SceneKey,
  TalkGroupKey,
  TalkKey,
} from '../type/KeyType.type';
import {PreloadedMedia} from '../type/PreloadedMedia.interface';
import {PreloadType} from '../type/PreloadType.enum';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkMediaStatus} from '../type/TalkMediaStatus.enum';
import {TalkViewerResponseVo} from '../type/TalkViewerResponse.interface';
import {VideoPlayType} from '../type/VIdeoPlayType.enum';
import {ViewerMenuHeight} from '../type/ViewerMenuHeight.interface';
import {ViewingMode} from '../type/ViewingMode.enum';
import {fetchTalkViewerContent} from '../util/fetchTalkViewerContent.thunk';
import {fetchTalkViewerContentFromAdminSite} from '../util/fetchTalkViewerContentFromAdminSite.thunk';
import {fetchTalkViewerContentFromCms} from '../util/fetchTalkViewerContentFromCms.thunk';
import {getTalkViewerInfo} from '../util/getTalkViewerInfo';
import {TalkViewerState} from './TalkViewerState.interface';

export const INITIAL_STATE: TalkViewerState = {
  settings: {
    mediaAutoPlay: false, // 웹은 기본적으로 유저 액션이 없이는 자동재생이 불가능하므로 false로 둔다. 웹뷰의 경우에는 첫 로딩에 앱에서 저장값을 받아 와서 사용한다.
    viewingMode: ViewingMode.Tap,
    preloadType: PreloadType.DataConverted,
    viewerMenuHeight: {
      top: DEFAULT_TOP_VIEWER_MENU_HEIGHT_PX,
      bottom: DEFAULT_BOTTOM_VIEWER_MENU_HEIGHT_PX,
    },
  },
  internalSettings: {
    autoSpeed: 1,
    muted: true,
    autoTap: AutoTapType.Pause,
  },
  settingMenu: {
    exposed: false,
  },
  loading: false,
  loaded: false,
  productInfoMap: {},
  sceneInfoMap: {},
  talkInfoMap: {},
  characterMap: {},
  sceneMap: {},
  talkGroupMap: {},
  talkMap: {},
  isPreview: false,
  videoPlayType: VideoPlayType.Web,
  isTutorialExposed: false,
  needMediaPlayedDirect: false,
  isDev: false,
  fontLoadStatus: FontLoadStatus.BeforeLoad,
  isHandlingSeekBar: false,
  isAutoTapTooltipExposed: false,
};

export const talkViewerSlice = createSlice({
  name: TALK_VIEWER_STATE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setVideoPlayType: (state, action: PayloadAction<VideoPlayType>) => {
      state.videoPlayType = action.payload;
    },
    setPreloadedMedia: (state, action: PayloadAction<PreloadedMedia>) => {
      state.preloadedMedia = action.payload;
    },
    appendTalk: (state, action: PayloadAction<TalkKey>) => {
      const {productInfoMap, talkMap, sceneInfoMap, talkInfoMap} = state;
      const talkId = action.payload;
      const talk = talkMap[talkId];
      if (!talk) {
        return;
      }
      const product = productInfoMap[talk.productId];
      if (!product) {
        return;
      }
      const {currentSceneId} = product;
      if (!currentSceneId) {
        return;
      }
      const currentSceneInfo = sceneInfoMap[currentSceneId];
      if (!currentSceneInfo) {
        return;
      }
      if (!currentSceneInfo.talkHistory) {
        currentSceneInfo.talkHistory = [];
      }
      talkInfoMap[talkId] = {
        talkId,
        isShowReplay: false,
      };
      currentSceneInfo.talkHistory.push(talkId);
      product.progress.visitedTalkCount = talkMap[talkId].talkIndex;
    },
    appendAllExtraTalks: state => {
      const {
        productInfoMap,
        sceneInfoMap,
        sceneMap,
        talkGroupMap,
        currentProductId,
        talkInfoMap,
        talkMap,
      } = state;

      if (!currentProductId) {
        return;
      }
      const product = productInfoMap[currentProductId];
      if (!product) {
        return;
      }
      const {currentSceneId} = product;
      if (!currentSceneId) {
        return;
      }
      const currentSceneInfo = sceneInfoMap[currentSceneId];
      if (!currentSceneInfo) {
        return;
      }
      const scene = sceneMap[currentSceneInfo.sceneId];
      let lastTalkId: TalkKey | undefined;

      if (currentSceneInfo.talkHistory && currentSceneInfo.talkHistory.length) {
        lastTalkId =
          currentSceneInfo.talkHistory[currentSceneInfo.talkHistory.length - 1];
      }

      let shouldAppend = lastTalkId ? false : true;
      scene.talkGroupIds.forEach(talkGroupId => {
        const talkGroupTalkIds = talkGroupMap[talkGroupId].talkIds;
        talkGroupTalkIds.forEach(talkId => {
          //  talkId loop도중 기존에 존재하던 톡 데이터타입이 배경음이면 그 배경음을 등록한다.
          if (talkMap[talkId]?.datatype === TalkDataType.Bgm && !shouldAppend) {
            state.bgm = {talkId};
          }
          if (talkId === lastTalkId) {
            // 마지막 톡 아이디를 찾았으니 다음 톡부터 뒤에 추가한다
            shouldAppend = true;
            return;
          }
          if (!shouldAppend) {
            return;
          }
          currentSceneInfo.talkHistory!.push(talkId);
          talkInfoMap[talkId] = {
            isShowReplay: false,
            talkId,
          };
        });
      });
    },
    changeTalkGroup: (state, action: PayloadAction<TalkGroupKey>) => {
      const {productInfoMap, talkGroupMap, sceneInfoMap} = state;
      const talkGroupId = action.payload;
      const talkGroup = talkGroupMap[talkGroupId];
      const product = productInfoMap[talkGroup.productId];
      if (!product) {
        return;
      }
      const {currentSceneId} = product;
      if (!currentSceneId) {
        return;
      }
      const currentScene = sceneInfoMap[currentSceneId];
      if (!currentScene) {
        return;
      }
      currentScene.talkGroupId = talkGroupId;
    },
    changeScene: (state, action: PayloadAction<SceneKey>) => {
      const {productInfoMap, sceneMap, talkGroupMap, sceneInfoMap} = state;
      const sceneId = action.payload;
      const toBeChangedScene = sceneMap[sceneId];
      if (!toBeChangedScene) {
        return;
      }
      const product = productInfoMap[toBeChangedScene.productId];
      if (!product) {
        return;
      }
      const talkGroup = talkGroupMap[toBeChangedScene.talkGroupIds[0]];

      const pastSceneId = product.currentSceneId;
      const pastScene = pastSceneId && sceneMap[pastSceneId];

      product.currentSceneId = sceneId;
      // 이전 씬으로 돌아갈 때의 처리
      if (pastScene && pastScene.prevSceneId === sceneId) {
        const storedTalkHistory =
          sceneInfoMap[toBeChangedScene.id]?.talkHistory;
        const storedTalkGroupId =
          sceneInfoMap[toBeChangedScene.id]?.talkGroupId;
        const hasStoredSceneInfo = storedTalkGroupId && storedTalkHistory;
        sceneInfoMap[toBeChangedScene.id] = {
          sceneId: toBeChangedScene.id,
          talkGroupId: hasStoredSceneInfo ? storedTalkGroupId : talkGroup.id,
          talkHistory: hasStoredSceneInfo ? storedTalkHistory : [],
        };
        return;
      }
      // 다음 씬으로 이동한다면, 항상 talkHistory를 초기화시킨다.
      sceneInfoMap[toBeChangedScene.id] = {
        sceneId: toBeChangedScene.id,
        talkGroupId: talkGroup.id,
        talkHistory: [],
      };
    },
    playAutoTap: state => {
      state.internalSettings.autoTap = AutoTapType.Play;
    },
    pauseAutoTap: state => {
      state.internalSettings.autoTap = AutoTapType.Pause;
    },
    stopAutoTap: state => {
      state.internalSettings.autoTap = AutoTapType.Stop;
    },
    setAutoTapSpeed: (state, action: PayloadAction<AutoTapSpeed>) => {
      state.internalSettings.autoSpeed = action.payload;
    },
    showFullscreen: (state, action: PayloadAction<Fullscreen>) => {
      state.fullscreen = action.payload;
    },
    hideFullscreen: state => {
      state.fullscreen = undefined;
    },
    playMedia: (state, action: PayloadAction<TalkKey>) => {
      const talkId = action.payload;
      const {
        talkMap,
        talkInfoMap,
        bgm,
        sound,
        movie,
        internalSettings: {muted},
      } = state;
      const talk = talkMap[talkId];
      if (talk.datatype === TalkDataType.Bgm) {
        if (bgm) {
          talkInfoMap[bgm.talkId].mediaStatus = TalkMediaStatus.Stop;
        }
        state.bgm = {talkId};
      } else if (
        talk.datatype === TalkDataType.Sound ||
        (talk.datatype === TalkDataType.TalkEffect && talk.media)
      ) {
        if (sound) {
          talkInfoMap[sound.talkId].mediaStatus = TalkMediaStatus.Stop;
        }
        state.sound = {talkId};
        // 효과음은 현재 muted 상태인 경우 Stop하고 return한다
        if (muted) {
          talkInfoMap[talkId].mediaStatus = TalkMediaStatus.Stop;
          return;
        }
      } else if (
        talk.datatype === TalkDataType.Movie ||
        talk.datatype === TalkDataType.FullscreenVideo
      ) {
        if (movie) {
          talkInfoMap[movie.talkId].mediaStatus = TalkMediaStatus.Stop;
        }
        state.movie = {talkId};
      }
      talkInfoMap[talkId].mediaStatus = TalkMediaStatus.Play;
    },
    pauseMedia: (state, action: PayloadAction<TalkKey>) => {
      const talkId = action.payload;
      const {talkInfoMap} = state;
      talkInfoMap[talkId].mediaStatus = TalkMediaStatus.Pause;
    },
    stopMedia: (state, action: PayloadAction<TalkKey>) => {
      const talkId = action.payload;
      const {talkInfoMap} = state;
      talkInfoMap[talkId].mediaStatus = TalkMediaStatus.Stop;
    },
    setIsMediaAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.settings.mediaAutoPlay = action.payload;
    },
    changeViewingMode: (state, action: PayloadAction<ViewingMode>) => {
      state.settings.viewingMode = action.payload;
    },
    showTalkReplayBox: (state, action: PayloadAction<TalkKey>) => {
      const talkId = action.payload;
      const {talkInfoMap} = state;
      const talkInfo = talkInfoMap[talkId];
      if (state.shownReplay) {
        const prevReplayTalkId = state.shownReplay.talkId;
        const prevTalkInfo = talkInfoMap[prevReplayTalkId];
        if (prevTalkInfo) {
          prevTalkInfo.isShowReplay = false;
        }
      }
      state.shownReplay = {talkId};
      talkInfo.isShowReplay = true;
    },
    hideTalkReplayBox: state => {
      const {talkInfoMap} = state;
      if (!state.shownReplay) {
        return;
      }
      const talkInfo = talkInfoMap[state.shownReplay.talkId];
      talkInfo.isShowReplay = false;
      state.shownReplay = undefined;
    },
    replayTalk: (state, _action: PayloadAction<TalkKey>) => {
      const {talkInfoMap} = state;
      for (const eachTalkId in talkInfoMap) {
        if (talkInfoMap[eachTalkId].isShowReplay === true) {
          talkInfoMap[eachTalkId].isShowReplay = false;
        }
      }
    },
    forceProcessToTalkIndex: (state, action: PayloadAction<number>) => {
      const {
        currentProductId,
        sceneInfoMap,
        productInfoMap,
        talkMap,
        talkInfoMap,
        talkGroupMap,
        sceneMap,
        settings,
        bgm: prevBgm,
      } = state;
      const receivedTalkIndex = action.payload;
      if (!currentProductId) {
        return;
      }
      const productInfo = productInfoMap[currentProductId];
      const totalTalkIndex = productInfo.progress.totalTalkCount;
      let talkIndex = Math.min(totalTalkIndex, receivedTalkIndex);

      if (receivedTalkIndex === 0) {
        const currentSceneId = productInfo.sceneIds[0];
        sceneInfoMap[currentSceneId] = {
          sceneId: currentSceneId,
          talkGroupId: sceneMap[currentSceneId].talkGroupIds[0],
          talkHistory: [],
        };
        productInfo.currentSceneId = currentSceneId;
        return;
      }

      const targetTalkId = Object.keys(talkMap).find(
        talkId =>
          talkMap[talkId].productId === currentProductId &&
          talkMap[talkId].talkIndex === talkIndex
      );
      if (!targetTalkId) {
        return;
      }
      let targetBgm = undefined;
      const isBgmTalk = talkMap[targetTalkId].datatype === TalkDataType.Bgm;
      const targetTalkGroupId = talkMap[targetTalkId].talkGroupId;
      const targetSceneId = talkGroupMap[targetTalkGroupId].sceneId;

      // loop가 이동하고자 하는 톡 이후에 도는 지 판별하기 위한 플래그
      let isAfterTargetTalk = false;
      productInfo.sceneIds.forEach(sceneId => {
        sceneInfoMap[sceneId].talkHistory = [];
        // 해당 톡 이후의 씬들은 처리하지 않는다.
        if (isAfterTargetTalk) {
          return;
        }
        const sceneTalkHistory: TalkKey[] = [];
        sceneMap[sceneId].talkGroupIds.forEach(talkGroupId => {
          // 해당 톡 이후의 톡그룹은 처리하지 않는다.
          if (isAfterTargetTalk) {
            return;
          }
          const talks = talkGroupMap[talkGroupId].talkIds;
          const targetTalkIndex = talks.findIndex(id => id === targetTalkId);

          // 탭모드인 경우에는 해당 톡까지만 talkHistory에 추가한 뒤 isAfterTargetTalk 플래그를 true로 만들어준다.
          if (
            targetTalkIndex >= 0 &&
            settings.viewingMode === ViewingMode.Tap
          ) {
            isAfterTargetTalk = true;
            sceneTalkHistory.push(...talks.slice(0, targetTalkIndex + 1));
            // 대상 톡이 배경음톡인 경우 배경음 등록 후 talkHistory에 바로 뒤 톡을 하나 더 추가한다. 만약 뒤 톡이 없으면 추가되지 않음
            if (isBgmTalk) {
              talkInfoMap[targetTalkId].mediaStatus = TalkMediaStatus.Stop;
              targetBgm = targetTalkId;
              sceneTalkHistory.push(
                ...talks.slice(targetTalkIndex + 1, targetTalkIndex + 2)
              );
              // 배경음 톡이 아니면 해당 씬의 가장 마지막 배경음 톡을 찾아서 등록한다.
            } else {
              const lastBgmTalk = sceneTalkHistory
                .slice()
                .reverse()
                .find(talkId => talkMap[talkId].datatype === TalkDataType.Bgm);
              if (lastBgmTalk) {
                talkInfoMap[lastBgmTalk].mediaStatus = TalkMediaStatus.Stop;
                targetBgm = lastBgmTalk;
              }
            }
            // 아직 해당 톡을 못찾았거나, 스크롤모드인 경우는 모든 톡을 talkHistory에 추가함
          } else {
            sceneTalkHistory.push(...talks);
          }
        });
        sceneInfoMap[sceneId].talkHistory = sceneTalkHistory;
        // 톡을 찾았는데 스크롤모드인 경우는 여기서 flag를 true로 만들어 줌
        if (
          sceneId === targetSceneId &&
          settings.viewingMode === ViewingMode.Scroll
        ) {
          const firstBgmTalk = talkGroupMap[targetTalkGroupId].talkIds.find(
            talkId => talkMap[talkId].datatype === TalkDataType.Bgm
          );
          // 스크롤 모드에서는 해당 톡그룹의 첫번째 bgm을 등록한다
          if (firstBgmTalk) {
            targetBgm = firstBgmTalk;
          }
          if (isBgmTalk) {
            talkIndex = Math.min(totalTalkIndex, receivedTalkIndex + 1);
          }
          isAfterTargetTalk = true;
        }
      });
      // 등록할 bgm이 있으면 등록, 그 외는 해제
      state.bgm = targetBgm ? {talkId: targetBgm} : undefined;
      // 기존 Bgm 상태 초기화
      if (prevBgm) {
        talkInfoMap[prevBgm.talkId].mediaStatus = TalkMediaStatus.Stop;
      }
      sceneInfoMap[targetSceneId].talkGroupId = targetTalkGroupId;
      productInfo.currentSceneId = targetSceneId;
      // 실제로 진행한 톡보다 받은 톡 index가 더 크면 뷰어엔드까지 진행한 경우이다. 이 경우 totalIndex+1을 저장해준다.
      productInfo.progress.visitedTalkCount =
        receivedTalkIndex > talkIndex ? totalTalkIndex + 1 : talkIndex;
    },
    setExposedTalk: (state, action: PayloadAction<Exposed>) => {
      const {productInfoMap, currentProductId, talkMap} = state;

      if (!currentProductId) {
        return;
      }
      const product = productInfoMap[currentProductId];
      if (!product) {
        return;
      }
      const {topTalkId, bottomTalkId, scrollOffset} = action.payload;
      product.exposed = {
        topTalkId,
        bottomTalkId,
        scrollOffset,
      };
      product.progress.visitedTalkCount = talkMap[bottomTalkId].talkIndex;
    },
    registerBgm: (state, action: PayloadAction<TalkKey>) => {
      const talkId = action.payload;
      const {talkInfoMap, bgm: prevBgm} = state;
      if (prevBgm && prevBgm.talkId !== talkId) {
        talkInfoMap[prevBgm.talkId].mediaStatus = TalkMediaStatus.Stop;
      }
      state.bgm = {talkId};
    },
    unregisterBgm: state => {
      state.bgm = undefined;
    },
    setMuted: (state, action: PayloadAction<boolean>) => {
      state.internalSettings.muted = action.payload;
    },
    setViewerMenuHeight: (state, action: PayloadAction<ViewerMenuHeight>) => {
      state.settings.viewerMenuHeight = action.payload;
    },
    setVisitedTalkCountToViewerEnd: state => {
      const {currentProductId, productInfoMap} = state;
      if (!currentProductId) {
        return;
      }
      const productInfo = productInfoMap[currentProductId];
      const totalTalkIndex = productInfo.progress.totalTalkCount;
      // 뷰어엔드 진입은 totalkTalkIndex + 1로 표현 해 준다.
      productInfo.progress.visitedTalkCount = totalTalkIndex + 1;
    },
    setPreviewEpisodeTitle: (state, action: PayloadAction<string>) => {
      state.previewEpisodeTitle = action.payload;
    },
    setPreviewTitle: (state, action: PayloadAction<string>) => {
      state.previewTitle = action.payload;
    },
    setIsTutorialExposed: (state, action: PayloadAction<boolean>) => {
      state.isTutorialExposed = action.payload;
    },
    initializeProductProgress: (state, action: PayloadAction<ProductKey>) => {
      const productId = action.payload;
      const targetProductInfo = state.productInfoMap[productId];
      if (targetProductInfo?.progress) {
        state.productInfoMap[productId].progress.visitedTalkCount = 0;
      }
    },
    setNeedMediaPlayedDirect: (state, action: PayloadAction<boolean>) => {
      state.needMediaPlayedDirect = action.payload;
    },
    setIsSettingMenuExposed: (state, action: PayloadAction<boolean>) => {
      state.settingMenu.exposed = action.payload;
    },
    talkViewerInfoFetched: (
      state,
      action: PayloadAction<{
        productId: ProductKey;
        triggerType: 'page_move' | 'reload';
        response: TalkViewerResponseVo;
        viewerInfoData: ViewerInfo;
      }>
    ) => {
      const {productId, response, viewerInfoData} = action.payload;
      state.error = undefined;
      const prevProductInfo = state.productInfoMap[productId];
      const {
        product,
        characterMap,
        sceneMap,
        talkGroupMap,
        talkMap,
        talkInfoMap,
        sceneInfoMap,
      } = getTalkViewerInfo(productId, response);
      state.productInfoMap[product.productId] = {
        ...product,
        // 기존에 보던 progress정보가 있다면, 해당 progress를 유(이어보기기능)
        progress: prevProductInfo?.progress || product.progress,
      };
      state.currentViewerInfoData = viewerInfoData;
      state.currentProductId = product.productId;
      assign(state.characterMap, characterMap);
      assign(state.sceneMap, sceneMap);
      assign(state.talkGroupMap, talkGroupMap);
      assign(state.talkMap, talkMap);
      assign(state.sceneInfoMap, sceneInfoMap);
      assign(state.talkInfoMap, talkInfoMap);
    },
    talkViewerInfoLoadingFinished: state => {
      state.loading = false;
      state.loaded = true;
      state.error = undefined;
    },
    setFontLoadStatus: (state, action: PayloadAction<FontLoadStatus>) => {
      state.fontLoadStatus = action.payload;
    },
    talkViewerErrorOccured: (
      state,
      action: PayloadAction<TalkViewerErrorType>
    ) => {
      state.loading = false;
      state.loaded = false;
      state.error = {
        isSerializedPagewebError: true,
        type: PagewebErrorType.TalkViewerError,
        talkViewerErrorType: action.payload,
        message: tt(TALK_VIEWER_ERROR_MESSAGE_MAP[action.payload]),
      };
    },
    clearTalkViewerError: state => {
      state.error = undefined;
    },
    setIsHandlingSeekBar: (state, action: PayloadAction<boolean>) => {
      state.isHandlingSeekBar = action.payload;
    },
    setIsAutoTapTooltipExposed: (state, action: PayloadAction<boolean>) => {
      state.isAutoTapTooltipExposed = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchTalkViewerContent.pending, state => {
      state.loaded = false;
      state.error = undefined;
      state.isPreview = false;
      state.isDev = false;
    });
    builder.addCase(fetchTalkViewerContentFromAdminSite.pending, state => {
      state.loading = true;
      state.loaded = false;
      state.error = undefined;
      state.isPreview = true;
      state.isDev = false;
    });
    builder.addCase(fetchTalkViewerContentFromCms.pending, state => {
      state.loading = true;
      state.loaded = false;
      state.error = undefined;
      state.isPreview = true;
      state.isDev = false;
    });
  },
});

export const {
  setVideoPlayType,
  setAutoTapSpeed,
  setExposedTalk,
  setIsMediaAutoPlay,
  setIsSettingMenuExposed,
  setMuted,
  setNeedMediaPlayedDirect,
  setPreloadedMedia,
  setPreviewEpisodeTitle,
  setPreviewTitle,
  setIsTutorialExposed,
  setViewerMenuHeight,
  showFullscreen,
  showTalkReplayBox,
  stopAutoTap,
  pauseMedia,
  stopMedia,
  appendAllExtraTalks,
  appendTalk,
  registerBgm,
  unregisterBgm,
  hideTalkReplayBox,
  changeScene,
  changeTalkGroup,
  changeViewingMode,
  forceProcessToTalkIndex,
  replayTalk,
  pauseAutoTap,
  playAutoTap,
  playMedia,
  talkViewerInfoFetched,
  hideFullscreen,
  talkViewerInfoLoadingFinished,
  setFontLoadStatus,
  setVisitedTalkCountToViewerEnd,
  talkViewerErrorOccured,
  clearTalkViewerError,
  setIsHandlingSeekBar,
  setIsAutoTapTooltipExposed,
} = talkViewerSlice.actions;

listenerMiddleware.startListening({
  actionCreator: setIsAutoTapTooltipExposed,
  effect: action => {
    // 툴팁 닫히는 시점에 로컬스토리지에 기록
    if (!action.payload) {
      localStorage.setItem(LOCAL_STORAGE_KEY_AUTO_TAP_TOOLTIP_HIDDEN, 'true');
    }
  },
});
